import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Apple, X, Plus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FoodManagement = ({ patient }) => {
  const [foodList, setFoodList] = useState({
    foods_to_avoid: [],
    foods_allowed: [],
  });
  const [newAvoid, setNewAvoid] = useState('');
  const [newAllowed, setNewAllowed] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (patient) {
      fetchFoodList();
    }
  }, [patient]);

  const fetchFoodList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/food-lists/${patient.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFoodList(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching food list:', error);
      setLoading(false);
    }
  };

  const addFood = async (type, food) => {
    if (!food.trim()) return;

    const token = localStorage.getItem('token');
    const updatedList = {
      ...foodList,
      [type]: [...foodList[type], food.trim()],
    };

    try {
      await axios.post(
        `${API}/food-lists`,
        {
          patient_id: patient.id,
          foods_to_avoid: updatedList.foods_to_avoid,
          foods_allowed: updatedList.foods_allowed,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFoodList(updatedList);
      if (type === 'foods_to_avoid') {
        setNewAvoid('');
      } else {
        setNewAllowed('');
      }
      toast.success('Food list updated');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || 'Food restrictions are a premium feature. Upgrade to access.');
      } else {
        toast.error('Failed to update food list');
      }
    }
  };

  const removeFood = async (type, index) => {
    const token = localStorage.getItem('token');
    const updatedList = {
      ...foodList,
      [type]: foodList[type].filter((_, i) => i !== index),
    };

    try {
      await axios.post(
        `${API}/food-lists`,
        {
          patient_id: patient.id,
          foods_to_avoid: updatedList.foods_to_avoid,
          foods_allowed: updatedList.foods_allowed,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFoodList(updatedList);
      toast.success('Food removed');
    } catch (error) {
      toast.error('Failed to remove food');
    }
  };

  if (loading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="food-management">
      {/* Foods to Avoid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card"
        style={{ backgroundColor: '#fff5f5', border: '2px solid #F9A7A7' }}
        data-testid="foods-to-avoid"
      >
        <div className="flex items-center gap-2 mb-4">
          <Apple size={20} style={{ color: '#F9A7A7' }} />
          <h3 className="text-xl font-semibold" style={{ color: '#F9A7A7' }}>Foods to Avoid</h3>
        </div>

        <div className="flex gap-2 mb-4">
          <input
            data-testid="avoid-food-input"
            type="text"
            value={newAvoid}
            onChange={(e) => setNewAvoid(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addFood('foods_to_avoid', newAvoid)}
            placeholder="Enter food to avoid"
            className="flex-1"
          />
          <button
            data-testid="add-avoid-food-button"
            onClick={() => addFood('foods_to_avoid', newAvoid)}
            className="btn btn-coral flex items-center gap-2 px-4"
          >
            <Plus size={16} />
          </button>
        </div>

        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {foodList.foods_to_avoid.length === 0 ? (
            <p className="text-gray-500 text-center py-8 text-sm">No foods added yet</p>
          ) : (
            foodList.foods_to_avoid.map((food, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 rounded-lg"
                style={{ backgroundColor: 'white', border: '1px solid #F9A7A7' }}
                data-testid={`avoid-food-${index}`}
              >
                <span className="text-sm">{food}</span>
                <button
                  data-testid={`remove-avoid-food-${index}`}
                  onClick={() => removeFood('foods_to_avoid', index)}
                  className="text-gray-400 hover:text-red-500 transition"
                >
                  <X size={16} />
                </button>
              </div>
            ))
          )}
        </div>
      </motion.div>

      {/* Foods Allowed */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card"
        style={{ backgroundColor: '#f0f9f8', border: '2px solid #A2DAD3' }}
        data-testid="foods-allowed"
      >
        <div className="flex items-center gap-2 mb-4">
          <Apple size={20} style={{ color: '#A2DAD3' }} />
          <h3 className="text-xl font-semibold" style={{ color: '#A2DAD3' }}>Foods Allowed</h3>
        </div>

        <div className="flex gap-2 mb-4">
          <input
            data-testid="allowed-food-input"
            type="text"
            value={newAllowed}
            onChange={(e) => setNewAllowed(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addFood('foods_allowed', newAllowed)}
            placeholder="Enter allowed food"
            className="flex-1"
          />
          <button
            data-testid="add-allowed-food-button"
            onClick={() => addFood('foods_allowed', newAllowed)}
            className="btn btn-primary flex items-center gap-2 px-4"
          >
            <Plus size={16} />
          </button>
        </div>

        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {foodList.foods_allowed.length === 0 ? (
            <p className="text-gray-500 text-center py-8 text-sm">No foods added yet</p>
          ) : (
            foodList.foods_allowed.map((food, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 rounded-lg"
                style={{ backgroundColor: 'white', border: '1px solid #A2DAD3' }}
                data-testid={`allowed-food-${index}`}
              >
                <span className="text-sm">{food}</span>
                <button
                  data-testid={`remove-allowed-food-${index}`}
                  onClick={() => removeFood('foods_allowed', index)}
                  className="text-gray-400 hover:text-red-500 transition"
                >
                  <X size={16} />
                </button>
              </div>
            ))
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default FoodManagement;
